import { datetimeItemFilterKeys } from '@/components/common/filters/types';
import { tempoApi } from '@/lib/api/tempoApi';
import type { DatetimeFilterKeys, ItemFiltersType } from '@/lib/api/types/itemFilters';
import type {
  BulkFetchV2Response,
  BulkPartialPatchRequest,
  BulkPartialPatchResponse,
  CreateItemRequest,
  Item,
  ItemAggregationRequest,
  ItemAggregationResponse,
  ItemAggregationsResults,
  ItemAncestor,
  ItemDataSet,
  ItemFieldDto,
  ItemPatchRequest,
  ItemRequestModelField,
  ItemTreeSet,
  PaginatedItems,
} from '@/lib/api/types/items';
import { API_MAX_PAGE_LIMIT } from '@/lib/constants';
import {
  and,
  containsIgnoreCase,
  equals,
  greaterThan,
  greaterThanOrEqualTo,
  includes,
  isNotNull,
  isNull,
  lessThan,
  lessThanOrEqualTo,
  type Operator,
  or,
  startsWithIgnoreCase,
} from '@/lib/utils/common/rsqlFilters';

export const getOperationType = (key: string, value: string | string[], operator: Operator) => {
  switch (operator) {
    case 'eq':
      if (Array.isArray(value)) {
        return includes(key, value);
      }
      return equals(key, value);
    case 'containsIgnoreCase':
      if (Array.isArray(value)) {
        return null;
      }
      return containsIgnoreCase(key, value);
    case 'startsWithIgnoreCase':
      if (Array.isArray(value)) {
        return null;
      }
      return startsWithIgnoreCase(key, value);
    case 'in':
      if (!Array.isArray(value)) {
        return equals(key, value);
      }
      if (Array.isArray(value) && value.length === 1) {
        return equals(key, value[0] ?? '');
      }
      return includes(key, Array.isArray(value) ? value : [value]);
    case 'null':
      return isNull(key);
    case 'notnull':
      return isNotNull(key);
    case 'gt':
      if (Array.isArray(value)) {
        return null;
      }
      return greaterThan(key, value);
    case 'ge':
      if (Array.isArray(value)) {
        return null;
      }
      return greaterThanOrEqualTo(key, value);
    case 'lt':
      if (Array.isArray(value)) {
        return null;
      }
      return lessThan(key, value);
    case 'le':
      if (Array.isArray(value)) {
        return null;
      }
      return lessThanOrEqualTo(key, value);
    default:
      if (Array.isArray(value)) {
        return null;
      }
      return equals(key, value);
  }
};

const isMultiDescendantFieldSearch = (filter: ItemFiltersType): boolean => {
  const hasDescendantName = filter['descendant.name']?.value;
  const hasDescendantExternalKey = filter['descendant.externalKey']?.value;

  return !!(
    hasDescendantName &&
    hasDescendantExternalKey &&
    filter['descendant.name']?.value === filter['descendant.externalKey']?.value &&
    filter['descendant.name']?.operator === 'containsIgnoreCase' &&
    filter['descendant.externalKey']?.operator === 'containsIgnoreCase'
  );
};

const isMultiFieldNameSearch = (filter: ItemFiltersType): boolean => {
  const hasName = filter.name?.value;
  const hasExternalKey = filter.externalKey?.value;

  return !!(
    hasName &&
    hasExternalKey &&
    filter.name?.value === filter.externalKey?.value &&
    filter.name?.operator === 'containsIgnoreCase' &&
    filter.externalKey?.operator === 'containsIgnoreCase'
  );
};

const handleNullFilter = (
  filterKey: string,
  filterValue: { operator?: string; value?: unknown },
): string | undefined => {
  if (filterValue.operator === 'null') {
    return isNull(filterKey);
  }
  if (filterValue.operator === 'notnull') {
    return isNotNull(filterKey);
  }
  if (filterValue.value === null) {
    return isNull(filterKey);
  }
  return undefined;
};

const handleDatetimeFilter = (
  filterKey: string,
  filterValue: { operator?: string; value?: unknown },
): string | undefined => {
  if (!datetimeItemFilterKeys.includes(filterKey as DatetimeFilterKeys)) {
    return undefined;
  }

  if (
    (filterValue.operator === 'in' || filterValue.operator === 'eq') &&
    Array.isArray(filterValue.value) &&
    filterValue.value.length === 2
  ) {
    return and(
      greaterThanOrEqualTo(filterKey, filterValue.value[0] as string),
      lessThanOrEqualTo(filterKey, filterValue.value[1] as string),
    );
  }

  return undefined;
};

const handleStringFilter = (
  filterKey: string,
  filterValue: { operator?: string; value?: unknown },
): string | undefined => {
  if (typeof filterValue.value !== 'string') {
    return undefined;
  }

  if (filterValue.operator === 'containsIgnoreCase') {
    return containsIgnoreCase(filterKey, filterValue.value);
  }
  if (filterValue.operator === 'startsWithIgnoreCase') {
    return startsWithIgnoreCase(filterKey, filterValue.value);
  }
  return equals(filterKey, filterValue.value);
};

const handleArrayFilter = (
  filterKey: string,
  filterValue: { operator?: string; value?: unknown },
): string | undefined => {
  if (!Array.isArray(filterValue.value)) {
    return undefined;
  }

  if (filterValue.value.length === 0) {
    return undefined;
  }

  if (filterValue.value.length === 1 && filterValue.value[0]) {
    return (
      getOperationType(
        filterKey,
        filterValue.value[0].toString(),
        filterValue.operator as Operator,
      ) ?? undefined
    );
  }

  return (
    getOperationType(filterKey, filterValue.value.map(String), filterValue.operator as Operator) ??
    undefined
  );
};

const handleGenericFilter = (
  filterKey: string,
  filterValue: { operator?: string; value?: unknown },
): string | undefined => {
  if (filterValue.value === undefined) {
    return undefined;
  }

  return (
    getOperationType(filterKey, String(filterValue.value), filterValue.operator as Operator) ??
    undefined
  );
};

const CUSTOM_FIELD_TYPE_MAP: Record<string, string> = {
  text: 'Text',
  date: 'Date',
  checked: 'Checked',
  picklist: 'PicklistChoiceId',
  userId: 'UserId',
  itemId: 'ItemId',
  link: 'Link',
};

const toDateOnly = (value: string): string => value.split('T')[0] ?? value;

const handleCustomFieldFilter = (
  filterKey: string,
  filterValue: { operator?: string; value?: unknown },
): string | undefined => {
  const match = filterKey.match(/^cf\.(\d+)\.(\w+)$/);
  if (!match) return undefined;

  const fieldId = match[1];
  const fieldType = match[2];
  const rsqlProperty = CUSTOM_FIELD_TYPE_MAP[fieldType ?? ''];
  if (!rsqlProperty) return undefined;

  const rsqlKey = `field${fieldId}${rsqlProperty}`;

  if (fieldType === 'text' || fieldType === 'link') {
    if (typeof filterValue.value === 'string' && filterValue.value.trim()) {
      return containsIgnoreCase(rsqlKey, filterValue.value.trim());
    }
    if (Array.isArray(filterValue.value) && filterValue.value.length > 0 && filterValue.value[0]) {
      return containsIgnoreCase(rsqlKey, String(filterValue.value[0]).trim());
    }
  }

  if (fieldType === 'date') {
    if (filterValue.operator === 'null') return isNull(rsqlKey);
    if (filterValue.operator === 'notnull') return isNotNull(rsqlKey);

    if (
      (filterValue.operator === 'in' || filterValue.operator === 'eq') &&
      Array.isArray(filterValue.value) &&
      filterValue.value.length === 2
    ) {
      return and(
        greaterThanOrEqualTo(rsqlKey, toDateOnly(filterValue.value[0] as string)),
        lessThanOrEqualTo(rsqlKey, toDateOnly(filterValue.value[1] as string)),
      );
    }

    if (typeof filterValue.value === 'string') {
      return (
        getOperationType(
          rsqlKey,
          toDateOnly(filterValue.value),
          filterValue.operator as Operator,
        ) ?? undefined
      );
    }

    if (
      Array.isArray(filterValue.value) &&
      filterValue.value.length === 1 &&
      filterValue.value[0]
    ) {
      return (
        getOperationType(
          rsqlKey,
          toDateOnly(filterValue.value[0] as string),
          filterValue.operator as Operator,
        ) ?? undefined
      );
    }
  }

  if (fieldType === 'checked') {
    const boolValue = Array.isArray(filterValue.value) ? filterValue.value[0] : filterValue.value;
    if (boolValue === 'true' || boolValue === 'false' || typeof boolValue === 'boolean') {
      return equals(rsqlKey, String(boolValue));
    }
  }

  if (fieldType === 'picklist' || fieldType === 'itemId') {
    if (Array.isArray(filterValue.value) && filterValue.value.length > 0) {
      if (filterValue.value.length === 1) {
        return equals(rsqlKey, String(filterValue.value[0]));
      }
      return includes(rsqlKey, filterValue.value.map(String));
    }
    if (typeof filterValue.value === 'string' || typeof filterValue.value === 'number') {
      return equals(rsqlKey, String(filterValue.value));
    }
  }

  if (fieldType === 'userId') {
    if (Array.isArray(filterValue.value) && filterValue.value.length > 0) {
      if (filterValue.value.length === 1) {
        return equals(rsqlKey, String(filterValue.value[0]));
      }
      return includes(rsqlKey, filterValue.value.map(String));
    }
    if (typeof filterValue.value === 'string' && filterValue.value.trim()) {
      return equals(rsqlKey, filterValue.value.trim());
    }
  }

  return undefined;
};

const buildSingleFilter = (filterKey: string, filterValueRaw: unknown): string | undefined => {
  if (!filterValueRaw || typeof filterValueRaw !== 'object') {
    return undefined;
  }

  const filterValue = filterValueRaw as { operator?: string; value?: unknown };

  return (
    handleCustomFieldFilter(filterKey, filterValue) ||
    handleNullFilter(filterKey, filterValue) ||
    handleDatetimeFilter(filterKey, filterValue) ||
    handleStringFilter(filterKey, filterValue) ||
    handleArrayFilter(filterKey, filterValue) ||
    handleGenericFilter(filterKey, filterValue)
  );
};

const buildMultiDescendantFieldSearchFilter = (filter: ItemFiltersType): string | undefined => {
  const searchValue = filter['descendant.name']?.value;
  if (!searchValue) return undefined;

  const searchFilter = or(
    containsIgnoreCase('descendant.name', searchValue),
    containsIgnoreCase('descendant.externalKey', searchValue),
  );

  const otherFilters = Object.entries(filter)
    .filter(
      ([filterKey]) => filterKey !== 'descendant.name' && filterKey !== 'descendant.externalKey',
    )
    .map(([filterKey, filterValue]) => buildSingleFilter(filterKey, filterValue))
    .filter((filterResult): filterResult is string => !!filterResult);

  const allFilters = [searchFilter, ...otherFilters];
  return allFilters.length > 0 ? and(...allFilters) : undefined;
};

const buildMultiFieldNameSearchFilter = (filter: ItemFiltersType): string | undefined => {
  const searchValue = filter.name?.value;
  if (!searchValue) return undefined;

  const searchFilter = or(
    containsIgnoreCase('name', searchValue),
    containsIgnoreCase('externalKey', searchValue),
  );

  const otherFilters = Object.entries(filter)
    .filter(([filterKey]) => filterKey !== 'name' && filterKey !== 'externalKey')
    .map(([filterKey, filterValue]) => buildSingleFilter(filterKey, filterValue))
    .filter((filterResult): filterResult is string => !!filterResult);

  const allFilters = [searchFilter, ...otherFilters];
  return allFilters.length > 0 ? and(...allFilters) : undefined;
};

const buildHasDependenciesFilter = (
  filter: ItemFiltersType,
): { rsql: string | undefined; remaining: ItemFiltersType } => {
  const { hasDependencies, ...remaining } = filter as ItemFiltersType & {
    hasDependencies?: { value?: string | string[] };
  };

  const value = Array.isArray(hasDependencies?.value)
    ? hasDependencies.value[0]
    : hasDependencies?.value;

  if (!value) {
    return { rsql: undefined, remaining };
  }

  const rsqlKey =
    value === 'predecessors'
      ? 'hasDependencies.predecessors'
      : value === 'successors'
        ? 'hasDependencies.successors'
        : value === 'any'
          ? 'hasDependencies'
          : undefined;

  if (!rsqlKey) {
    return { rsql: undefined, remaining };
  }

  return {
    rsql: equals(rsqlKey, 'true'),
    remaining,
  };
};

const buildNeedsTeamOrEstimateFilter = (
  filter: ItemFiltersType,
): { rsql: string | undefined; remaining: ItemFiltersType } => {
  const { needsTeamOrEstimate, ...remaining } = filter as ItemFiltersType & {
    needsTeamOrEstimate?: { value?: boolean };
  };

  if (!needsTeamOrEstimate?.value) {
    return { rsql: undefined, remaining };
  }

  return {
    rsql: or(equals('descendant.teamId', '0'), equals('descendant.estimationId', '0')),
    remaining,
  };
};

const buildImportedItemsFilter = (
  filter: ItemFiltersType,
): { rsql: string | undefined; remaining: ItemFiltersType } => {
  const { importedItems, ...remaining } = filter as ItemFiltersType & {
    importedItems?: { value?: boolean };
  };

  if (!importedItems?.value) {
    return { rsql: undefined, remaining };
  }

  return {
    rsql: isNotNull('descendant.externalKey'),
    remaining,
  };
};

const buildLocallyCreatedItemsFilter = (
  filter: ItemFiltersType,
): { rsql: string | undefined; remaining: ItemFiltersType } => {
  const { locallyCreatedItems, ...remaining } = filter as ItemFiltersType & {
    locallyCreatedItems?: { value?: boolean };
  };

  if (!locallyCreatedItems?.value) {
    return { rsql: undefined, remaining };
  }

  return {
    rsql: isNull('descendant.externalKey'),
    remaining,
  };
};

export const buildFilterString = (filter?: ItemFiltersType): string | undefined => {
  if (!filter) return undefined;

  const { rsql: needsTeamOrEstimateRsql, remaining: afterQuickFilter } =
    buildNeedsTeamOrEstimateFilter(filter);
  const { rsql: importedItemsRsql, remaining: afterImported } =
    buildImportedItemsFilter(afterQuickFilter);
  const { rsql: locallyCreatedItemsRsql, remaining: afterLocal } =
    buildLocallyCreatedItemsFilter(afterImported);
  const { rsql: hasDependenciesRsql, remaining } = buildHasDependenciesFilter(afterLocal);

  const extractedRsqls = [
    needsTeamOrEstimateRsql,
    importedItemsRsql,
    locallyCreatedItemsRsql,
    hasDependenciesRsql,
  ].filter(Boolean) as string[];

  if (isMultiDescendantFieldSearch(remaining)) {
    const multiFieldRsql = buildMultiDescendantFieldSearchFilter(remaining);
    if (!multiFieldRsql && extractedRsqls.length === 0) return undefined;
    const parts = [multiFieldRsql, ...extractedRsqls].filter(Boolean) as string[];
    return and(...parts);
  }

  if (isMultiFieldNameSearch(remaining)) {
    const multiFieldRsql = buildMultiFieldNameSearchFilter(remaining);
    if (!multiFieldRsql && extractedRsqls.length === 0) return undefined;
    const parts = [multiFieldRsql, ...extractedRsqls].filter(Boolean) as string[];
    return and(...parts);
  }

  const filters = Object.entries(remaining)
    .map(([filterKey, filterValue]) => buildSingleFilter(filterKey, filterValue))
    .filter((filterResult): filterResult is string => !!filterResult);

  filters.push(...extractedRsqls);

  if (filters.length === 0) return undefined;

  return and(...filters);
};

const buildItemSearchQueryPrams = ({
  cursor,
  filter,
  limit,
  order,
  hierarchical,
}: {
  cursor?: string;
  filter?: ItemFiltersType;
  limit?: number;
  order?: string;
  hierarchical?: boolean;
}) => {
  const params = new URLSearchParams();

  if (cursor) {
    params.set('cursor', cursor);
  }

  if (order) {
    params.set('order', order);
  }

  if (filter) {
    const filterString = buildFilterString(filter);
    if (filterString) {
      params.set('filter', filterString);
    }
  }

  if (limit) {
    params.set('limit', String(limit));
  }

  if (hierarchical !== undefined) {
    params.set('hierarchical', String(hierarchical));
  }

  return params.toString() ? `?${params.toString()}` : '';
};

const getItems = async ({
  cursor = '',
  filter,
  limit,
  order = 'priority',
  hierarchical,
}: {
  cursor?: string;
  filter?: ItemFiltersType;
  limit?: number;
  order?: string;
  hierarchical?: boolean;
}): Promise<PaginatedItems> => {
  const url = `api/v1/item-store/items${buildItemSearchQueryPrams({ cursor, filter, limit, order, hierarchical })}`;
  return await tempoApi().get(url).json();
};

const getItemsDataSet = async ({
  order,
  filter,
}: {
  filter?: ItemFiltersType;
  order?: string;
}): Promise<ItemDataSet> => {
  const url = `api/v1/item-store/items/data-set${buildItemSearchQueryPrams({ filter, order })}`;
  return await tempoApi().get(url).json();
};

const getItemsTreeSet = async ({
  filter,
  order,
  includeDescendants,
}: {
  filter?: ItemFiltersType;
  order?: string;
  includeDescendants?: boolean;
}): Promise<ItemTreeSet> => {
  const filterString = buildFilterString(filter);
  return await tempoApi()
    .post('api/v1/item-store/items/tree-set', {
      json: { filter: filterString, order, includeDescendants },
    })
    .json();
};

const getInsightsItemsDataSet = async ({
  order,
  filterString = '',
}: {
  filterString?: string;
  order?: string;
}): Promise<ItemDataSet> => {
  const url =
    'api/v1/item-store/items/data-set' +
    (order ? `?order=${order}` : '') +
    (filterString ? `?filter=${encodeURIComponent(filterString)}` : '');

  return await tempoApi().get(url).json();
};

const getItemsBulkFetch = async ({
  ids,
  fields,
  customFieldIds,
  filter,
  signal,
}: {
  ids: number[];
  fields?: ItemRequestModelField[];
  customFieldIds?: number[];
  filter?: ItemFiltersType;
  signal?: AbortSignal;
}) => {
  const searchParams = new URLSearchParams();
  const filterString = buildFilterString(filter);
  if (filterString) {
    searchParams.set('filter', filterString);
  }

  return await tempoApi()
    .post('api/v1/item-store/items/bulk-fetch', {
      json: {
        ids,
        fields: fields ?? null,
        customFieldIds: customFieldIds ?? null,
      },
      searchParams,
      signal,
    })
    .json<Item[]>();
};

const getItemsBulkFetchV2 = async ({
  ids,
  fieldIds,
  customFieldIds,
  signal,
}: {
  ids: number[];
  fieldIds?: string[];
  customFieldIds?: number[];
  signal?: AbortSignal;
}) =>
  await tempoApi()
    .post('api/v1/item-store/items/bulk-fetch/v2', {
      json: {
        ids,
        fieldIds: fieldIds ?? null,
        customFieldIds: customFieldIds ?? null,
      },
      signal,
    })
    .json<BulkFetchV2Response>();

const BULK_FETCH_CONCURRENCY_LIMIT = 6;

// Chunked variant — splits requests into `API_MAX_PAGE_LIMIT`-sized batches and
// merges the responses. Use when callers may exceed the backend's per-request
// id limit (e.g. bulk edit, viewport-driven prefetch).
const getItemsBulkFetchV2Chunked = async (params: {
  ids: number[];
  fieldIds?: string[];
  customFieldIds?: number[];
  signal?: AbortSignal;
}): Promise<BulkFetchV2Response> => {
  const { ids, ...rest } = params;
  if (ids.length === 0) {
    return { itemsById: {} };
  }
  if (ids.length <= API_MAX_PAGE_LIMIT) {
    return getItemsBulkFetchV2({ ids, ...rest });
  }
  const chunks: number[][] = [];
  for (let i = 0; i < ids.length; i += API_MAX_PAGE_LIMIT) {
    chunks.push(ids.slice(i, i + API_MAX_PAGE_LIMIT));
  }
  // Cap simultaneous requests so a large id list doesn't burst past item-store's
  // per-user rate limit.
  const responses: BulkFetchV2Response[] = [];
  for (let i = 0; i < chunks.length; i += BULK_FETCH_CONCURRENCY_LIMIT) {
    const batch = chunks.slice(i, i + BULK_FETCH_CONCURRENCY_LIMIT);
    const batchResponses = await Promise.all(
      batch.map((chunk) => getItemsBulkFetchV2({ ids: chunk, ...rest })),
    );
    responses.push(...batchResponses);
  }
  return { itemsById: Object.assign({}, ...responses.map((r) => r.itemsById)) };
};

const getItemById = async ({ itemId }: { itemId: number }) =>
  await tempoApi().get(`api/v1/item-store/items/${itemId}`).json<Item>();

const getItemAncestors = async ({ itemId }: { itemId: number }) =>
  await tempoApi().get(`api/v1/item-store/items/${itemId}/ancestors`).json<ItemAncestor[]>();

const updateItem = async ({ itemId, data }: { itemId: number; data: ItemPatchRequest }) =>
  await tempoApi().patch(`api/v1/item-store/items/${itemId}`, { json: data }).json<Item>();

const bulkUpdateItems = async (items: Array<{ id: number } & ItemPatchRequest>) =>
  await tempoApi().patch('api/v1/item-store/items/bulk', { json: items }).json<Item[]>();

const createItem = async (data: CreateItemRequest) =>
  await tempoApi().post('api/v1/item-store/items', { json: data }).json<Item>();

const deleteItem = async ({ itemId }: { itemId: number }): Promise<void> => {
  await tempoApi().delete(`api/v1/item-store/items/${itemId}`);
};

const getItemAggregations = async (
  request: ItemAggregationRequest,
): Promise<ItemAggregationsResults[]> => {
  const response = await tempoApi()
    .post('api/v1/item-store/items/aggregations', { json: request })
    .json<ItemAggregationResponse>();
  return response.data;
};

const searchItemsWithCustomFields = async (): Promise<Item[]> => {
  const allItems: Item[] = [];
  let cursor: string | undefined;

  // Fetch all pages to ensure we see all custom-field values across the portfolio
  // Assumes `getItems` returns a paginated response with a `nextCursor` field.
  // We keep requesting pages until `nextCursor` is null/undefined.
  // If the backend ever changes pagination shape, this should be updated accordingly.
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const result = await getItems({
      limit: 100,
      hierarchical: false,
      cursor,
    });

    allItems.push(...result.data);

    // Stop if there is no further page
    if (!('nextCursor' in result) || result.nextCursor == null) {
      break;
    }

    cursor = result.nextCursor as string | undefined;
  }

  return allItems;
};

const getItemFields = async (): Promise<ItemFieldDto[]> =>
  await tempoApi().get('api/v1/item-store/items/fields').json<ItemFieldDto[]>();

const bulkPartialPatchItems = (
  request: BulkPartialPatchRequest,
): Promise<BulkPartialPatchResponse> =>
  tempoApi()
    .patch('api/v1/item-store/items/bulk/v2', { json: request })
    .json<BulkPartialPatchResponse>();

export const ItemsApi = {
  getItems,
  getItemsDataSet,
  getItemsTreeSet,
  getInsightsItemsDataSet,
  getItemsBulkFetch,
  getItemsBulkFetchV2,
  getItemsBulkFetchV2Chunked,
  getItemById,
  getItemAncestors,
  getItemFields,
  updateItem,
  bulkUpdateItems,
  createItem,
  deleteItem,
  getItemAggregations,
  searchItemsWithCustomFields,
  bulkPartialPatchItems,
};
